import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { BehaviorSubject, Subject, takeUntil } from 'rxjs';
import { CourseService } from '../../../domains/academy/course/services/course.service';
import { AsyncPipe } from '@angular/common';
import { TranslocoPipe } from '@jsverse/transloco';
import { EnrolledStudentTableComponent } from '../../../domains/academy/course/components/enrolled-student-table/enrolled-student-table.component';
import { CourseDataService } from '../../../domains/academy/course/services/course-data.service';
import { CourseInstanceUIModel } from '../../../domains/academy/course/models/CourseInstanceUIModel';
import { toCourseInstanceUIModel } from '../../../domains/academy/course/mappers/dto-mappers';
import { CourseStatisticsComponent } from '../../../domains/academy/course/components/course-statistics/course-statistics.component';
import { CustomPortalError } from '../../../core/errors/models/CustomPortalError';
import { SnackbarService } from '../../../core/services/snackbar.service';
import { EnrollmentNotificationService } from '../../../core/services/enrollment-notification.service';

@Component({
  selector: 'app-course-detail',
  imports: [
    RouterModule,
    AsyncPipe,
    TranslocoPipe,
    EnrolledStudentTableComponent,
    CourseStatisticsComponent,
  ],
  templateUrl: './course-detail.component.html',
  styleUrl: './course-detail.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CourseDetailComponent implements OnInit, OnDestroy {
  private readonly courseService = inject(CourseService);
  private readonly courseDataService = inject(CourseDataService);
  private readonly route = inject(ActivatedRoute);
  private readonly courseEnrollementNotificationService = inject(
    EnrollmentNotificationService,
  );
  private readonly snackbarService = inject(SnackbarService);

  translocoCourseDetailKeyPrefix = 'portal.courses.courseTitleKeys.';
  courseDetails$: BehaviorSubject<CourseInstanceUIModel | null> =
    new BehaviorSubject<CourseInstanceUIModel | null>(null);
  private readonly destroy$: Subject<void> = new Subject();

  ngOnInit(): void {
    this.observeCourseIdInUrlAndFetch();

    /*Data needs to be refetched when new students when enrollment changed (enroll/unenroll).
     Therefore we subscribe to the course enrollment notification service.*/
    this.courseEnrollementNotificationService
      .getEnrollmentChanged()
      .pipe(takeUntil(this.destroy$))
      .subscribe((enrollmentChanged) => {
        if (enrollmentChanged) {
          const courseId = this.courseDataService.getCourseId();
          if (courseId) {
            this.getCourseDetail(courseId);
          }
        }
      });
  }

  private observeCourseIdInUrlAndFetch(): void {
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      this.courseDetails$.next(null);
      const id = params.get('id');
      if (id) {
        this.courseDataService.setCourseId(id);
        this.getCourseDetail(id);
      }
    });
  }

  getCourseDetail(id: string) {
    this.courseService.getCourseDetail(id).subscribe({
      next: (courseInstanceDetailResponse) => {
        const courseDetails = toCourseInstanceUIModel(
          courseInstanceDetailResponse,
        );
        this.courseDetails$.next(courseDetails);
        this.courseDataService.setCourseName(courseDetails.course.name);
      },
      error: (error: CustomPortalError) => {
        this.snackbarService.error(error.errorType);
      },
    });
  }

  ngOnDestroy() {
    this.courseDetails$.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
